import React, { useMemo } from 'react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useGetShippingReminder } from '@/hooks/item';
import { ShippingReminderModal } from '@/types/modal';
import {
  BodyModal,
  CloseFooterModal,
  CustomModal,
  HeadingModal,
  LoadingBodyModal,
} from '@/components/molecules/common/Modal';
import styles from './Modal.module.scss';

const ShippingReminder: React.FC<{ modal: ShippingReminderModal }> = ({ modal }) => {
  const { t } = useTranslation();

  const { productId, onClose } = modal;

  const { data: shippingReminder, isLoading } = useGetShippingReminder(productId);

  const getSellerValue = (value?: boolean) => {
    if (value === undefined || value === null) return '';
    return value ? t('item.shipping_status.shippingReminder.sent') : t('item.shipping_status.shippingReminder.notSent');
  };

  const data = useMemo(() => {
    if (!shippingReminder) return [];

    const { seller, buyer } = shippingReminder;

    if (!seller && !buyer) return [];

    const sellerDates = seller
      ? [
          seller.isReminderD1Sent,
          seller.isReminderD2Sent,
          seller.isReminderD3Sent,
          seller.isReminderD4Sent,
          seller.isReminderD5Sent,
          seller.isReminderD6Sent,
        ]
          .map((d, idx) => {
            const value = getSellerValue(d);
            if (!value) return null;
            return {
              label: t(`item.shipping_status.shippingReminder.sellerReminder${idx + 1}`),
              value,
            };
          })
          .filter((i) => !!i)
      : [];

    const buyerDates = buyer
      ? [
          buyer.isReminderD1Sent,
          buyer.isReminderD2Sent,
          buyer.isReminderD3Sent,
          buyer.isReminderD4Sent,
          buyer.isReminderD5Sent,
          buyer.isReminderD6Sent,
        ]
          .map((d, idx) => {
            const value = getSellerValue(d);
            if (!value) return null;
            return {
              label: t(`item.shipping_status.shippingReminder.buyerReminder${idx + 1}`),
              value,
            };
          })
          .filter((i) => !!i)
      : [];

    return [
      {
        label: t('item.shipping_status.shippingReminder.timeLeftSellerToShip'),
        value: seller?.timeLeftToShip || '-',
      },
      {
        label: t('item.shipping_status.shippingReminder.timeToAutoCancellation'),
        value: seller?.timeToAutoCancellation || '-',
      },
      ...sellerDates,
      ...buyerDates,
    ];
  }, [shippingReminder]);

  const body = isLoading ? (
    <LoadingBodyModal height={60} />
  ) : (
    <div className={clsx([styles.text0875rem, styles.paddingY1rem, styles.shippingReminder_container])}>
      <p>{`Item #${productId}`}</p>

      <p>{t('item.shipping_status.shippingReminder.description')}</p>

      <div>
        <div
          className={styles.shippingReminder_row}
          style={{ fontWeight: 'bold' }}
        >
          <div className={styles.shippingReminder_label}>{t('item.shipping_status.shippingReminder.category')}</div>
          <div className={styles.shippingReminder_value}>{t('item.shipping_status.shippingReminder.information')}</div>
        </div>

        {data.length ? (
          data.map((d, idx) => {
            return (
              <div
                key={idx}
                className={styles.shippingReminder_row}
              >
                <div className={styles.shippingReminder_label}>{d.label}</div>
                <div className={styles.shippingReminder_value}>{d.value}</div>
              </div>
            );
          })
        ) : (
          <div className={styles.shippingReminder_noData}>{t('item.shipping_status.shippingReminder.noData')}</div>
        )}
      </div>
    </div>
  );

  return (
    <CustomModal
      onClose={onClose}
      body={
        <>
          <HeadingModal
            title={t('item.shipping_status.actions.shippingReminder')}
            onClose={onClose}
          />

          <BodyModal body={body} />

          <CloseFooterModal
            onClose={onClose}
            closeLabel={t('common.ok').toUpperCase()}
          />
        </>
      }
      containerStyle={{ width: 600 }}
      modalName="shippingReminder"
    />
  );
};

export default ShippingReminder;
